// services/studyYears.service.ts

import {
  GetStudyYearsRequest,
  StudyYearsResponse,
  StudyYearDetailResponse,
  UpdateStudyYearRequest,
  UpdateStudyYearResponse,
  PublishStudyYearRequest,
  PublishStudyYearResponse,
  GetStudyYearPublicationsResponse,
  GetStudyYearSegmentsResponse,
  AddStudyYearSegmentRequest,
  AddStudyYearSegmentResponse,
  GetStudyYearCategoriesResponse,
  DeleteStudyYearSegmentRequest,
  GetStudyYearCompaniesResponse,
  AddStudyYearCompanyRequest,
  AddStudyYearCompanyResponse,
  DeleteStudyYearCompanyRequest,
  GetStudyYearForecastsResponse,
  UpdateStudyYearForecastsRequest,
  UpdateStudyYearForecastsResponse,
  GetStudyYearChangesResponse,
} from '@/types/study-years';

import { api } from './index';

class StudyYearsService {
  // Core Study Years
  async getStudyYears(params?: GetStudyYearsRequest): Promise<StudyYearsResponse> {
    try {
      const response = await api.get<StudyYearsResponse>('/study-years/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching study years:', error);
      throw error;
    }
  }

  async getStudyYearById(studyYearId: string): Promise<StudyYearDetailResponse> {
    try {
      const response = await api.get<StudyYearDetailResponse>(`/study-years/${studyYearId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year ${studyYearId}:`, error);
      throw error;
    }
  }

  async updateStudyYear(request: UpdateStudyYearRequest): Promise<UpdateStudyYearResponse> {
    try {
      const { study_year_id, ...payload } = request;
      const response = await api.patch<UpdateStudyYearResponse>(
        `/study-years/${study_year_id}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error updating study year:', error);
      throw error;
    }
  }

  // Study Year Management
  async publishStudyYear(
    studyYearId: string,
    request: PublishStudyYearRequest
  ): Promise<PublishStudyYearResponse> {
    try {
      const response = await api.post<PublishStudyYearResponse>(
        `/study-years/${studyYearId}/publish`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error publishing study year:', error);
      throw error;
    }
  }

  async getStudyYearPublications(studyYearId: string): Promise<GetStudyYearPublicationsResponse> {
    try {
      const response = await api.get<GetStudyYearPublicationsResponse>(
        `/study-years/${studyYearId}/publications`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year publications for ${studyYearId}:`, error);
      throw error;
    }
  }

  async getStudyYearChanges(studyYearId: string): Promise<GetStudyYearChangesResponse> {
    try {
      const response = await api.get<GetStudyYearChangesResponse>(
        `/study-years/${studyYearId}/changes`
      );
      console.log(`Fetched changes for study year ${studyYearId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year changes for ${studyYearId}:`, error);
      throw error;
    }
  }

  async downloadStudyYearExcel(studyYearId: string): Promise<Blob> {
    try {
      const response = await api.get(`/study-years/${studyYearId}/download-excel`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error(`Error downloading Excel for study year ${studyYearId}:`, error);
      throw error;
    }
  }

  // Segments Management
  async getStudyYearSegments(studyYearId: string): Promise<GetStudyYearSegmentsResponse> {
    try {
      const response = await api.get<GetStudyYearSegmentsResponse>(
        `/study-years/${studyYearId}/segments`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year segments for ${studyYearId}:`, error);
      throw error;
    }
  }

  async addStudyYearSegment(
    studyYearId: string,
    request: AddStudyYearSegmentRequest
  ): Promise<AddStudyYearSegmentResponse> {
    try {
      const response = await api.post<AddStudyYearSegmentResponse>(
        `/study-years/${studyYearId}/segments`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error adding study year segment:', error);
      throw error;
    }
  }

  async getStudyYearCategories(studyYearId: string): Promise<GetStudyYearCategoriesResponse> {
    try {
      const response = await api.get<GetStudyYearCategoriesResponse>(
        `/study-years/${studyYearId}/categories`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year categories for ${studyYearId}:`, error);
      throw error;
    }
  }

  async deleteStudyYearSegment(
    studyYearId: string,
    studyYearCategoryId: string,
    request: DeleteStudyYearSegmentRequest
  ): Promise<void> {
    try {
      await api.post(`/study-years/${studyYearId}/segments/${studyYearCategoryId}/delete`, request);
    } catch (error) {
      console.error('Error deleting study year segment:', error);
      throw error;
    }
  }

  // Companies Management
  async getStudyYearCompanies(studyYearId: string): Promise<GetStudyYearCompaniesResponse> {
    try {
      const response = await api.get<GetStudyYearCompaniesResponse>(
        `/study-years/${studyYearId}/companies`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year companies for ${studyYearId}:`, error);
      throw error;
    }
  }

  async addStudyYearCompany(
    studyYearId: string,
    request: AddStudyYearCompanyRequest
  ): Promise<AddStudyYearCompanyResponse> {
    console.log(request);
    try {
      const response = await api.post<AddStudyYearCompanyResponse>(
        `/study-years/${studyYearId}/companies`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error adding study year company:', error);
      throw error;
    }
  }

  async deleteStudyYearCompany(
    studyYearId: string,
    studyYearCompanyId: string,
    request: DeleteStudyYearCompanyRequest
  ): Promise<void> {
    try {
      await api.post(`/study-years/${studyYearId}/companies/${studyYearCompanyId}/delete`, request);
    } catch (error) {
      console.error('Error deleting study year company:', error);
      throw error;
    }
  }

  // Forecasts Management
  async getStudyYearForecasts(studyYearId: string): Promise<GetStudyYearForecastsResponse> {
    try {
      const response = await api.get<GetStudyYearForecastsResponse>(
        `/study-years/${studyYearId}/forecasts`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year forecasts for ${studyYearId}:`, error);
      throw error;
    }
  }

  async updateStudyYearForecasts(
    studyYearId: string,
    request: UpdateStudyYearForecastsRequest
  ): Promise<UpdateStudyYearForecastsResponse> {
    try {
      const response = await api.patch<UpdateStudyYearForecastsResponse>(
        `/study-years/${studyYearId}/forecasts`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error updating study year forecasts:', error);
      throw error;
    }
  }
}

export const studyYearsService = new StudyYearsService();
